import { useState } from 'react'
import type { NtCourse } from '../../core/types'
import { decideCourse, planObservations, unplannedParts, STATUS_LABEL, activity, DAY_LABELS, SESSION_LABELS, segmentsIn, costTotal } from './model'
import { Area } from '../../ui/Field'
import { Empty, ErrorState } from '../../ui/States'
import { ArtifactList } from './ArtifactOptions'

/**
 * Course review — approve, or send back with a note.
 *
 * "Send back" requires a note, which is the whole design. A course returned
 * with no reason becomes an email thread, then a hallway conversation, then a
 * course that quietly does not run. The observations panel is computed rather
 * than judged: it says four of eight sessions are empty, and lets a human
 * decide whether that matters.
 */
export function ReviewQueue({ courses, who }: { courses: NtCourse[]; who: string }) {
  const waiting = courses.filter((c) => c.status === 'submitted')
  const done = courses.filter((c) => c.status !== 'submitted' && c.status !== 'draft')

  return (
    <>
      <h2 className="work__heading">
        Waiting on you {waiting.length > 0 && <span className="work__badge">{waiting.length}</span>}
      </h2>
      {waiting.length === 0
        ? <Empty title="Nothing to review" body="Submitted courses land here." />
        : waiting.map((c) => <ReviewCard key={c.id} course={c} who={who} />)}

      {done.length > 0 && (
        <>
          <h2 className="work__heading">Decided</h2>
          {done.map((c) => (
            <div key={c.id} className="work__row">
              <div className="work__main">
                <div style={{ fontWeight: 610 }}>{c.title}</div>
                <div className="work__sub">{c.teacherName}</div>
              </div>
              <span className={`pill ${c.status === 'approved' ? 'pill--good' : 'pill--serious'}`}>
                {STATUS_LABEL[c.status]}
              </span>
            </div>
          ))}
        </>
      )}
    </>
  )
}

function ReviewCard({ course, who }: { course: NtCourse; who: string }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const notes = planObservations(course)

  const decide = (approve: boolean) => async () => {
    if (!approve && !note.trim()) {
      setErr('Say what has to change. A course sent back with no reason becomes an email thread.')
      return
    }
    setBusy(true); setErr(null)
    try { await decideCourse(course.id, approve, who, approve ? note.trim() : note.trim()) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false) }
  }

  return (
    <section className="card card--action">
      <h3 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{course.title}</h3>
      <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--t-sm)', margin: '2px 0 var(--s-3)' }}>
        {course.teacherName} · {course.room || 'no room yet'} · seats {course.min}–{course.cap}
        {course.constraint !== 'none' && ` · wants ${course.constraint}`}
      </p>

      <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)' }}>{course.desc}</p>

      {course.costItems.length > 0 && (
        <div style={{ marginTop: 'var(--s-2)' }}>
          <p style={{ fontSize: 'var(--t-sm)', margin: '0 0 4px' }}>
            <strong>${costTotal(course.costItems)} to approve:</strong>
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--t-sm)', color: 'var(--ink-2)' }}>
            {course.costItems.map((it, i) => (
              <li key={i}>{it.label || 'Untitled item'}: ${it.amount}</li>
            ))}
          </ul>
        </div>
      )}

      {course.plan.length > 0 && (
        <div style={{ marginTop: 'var(--s-3)' }}>
          {(DAY_LABELS as readonly string[]).map((label, d) => {
            const both = [0, 1].flatMap((s) => segmentsIn(course.plan, d as 0 | 1 | 2 | 3, s as 0 | 1)
              .map((p) => ({ ...p, sLabel: SESSION_LABELS[s as 0 | 1] })))
            if (!both.length) return null
            return (
              <div key={label} className="cat__day">
                <div className="cat__dayname">{label}</div>
                {both.map((p, i) => (
                  <div key={i} className="cat__seg">
                    <span className="plan__dot" style={{ background: activity(p.kind).colour }} aria-hidden="true" />
                    <span style={{ fontSize: 'var(--t-sm)' }}>{p.text}</span>
                    <span className="cat__meta">{d === 3 ? 'Friday prep' : `${p.sLabel} · ${p.span > 1 ? 'both parts' : `part ${p.slot + 1}`}`}</span>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {course.needs && <p className="opt__hint"><strong>Arranged:</strong> {course.needs}</p>}
      <div style={{ margin: 'var(--s-2) 0' }}>
        <div className="opt__label">Artifact options</div>
        <ArtifactList options={course.artifactOptions} />
      </div>

      {/* Computed, not judged. It reports; a person decides. */}
      {notes.length > 0 && (
        <div className="notice notice--info" style={{ marginTop: 'var(--s-3)' }}>
          <span aria-hidden="true">i</span>
          <div>
            <strong>{unplannedParts(course.plan)} of 6 parts unplanned.</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
        </div>
      )}

      {err && <ErrorState body={err} />}

      <Area label="Note to the teacher (required to send back)" value={note} onChange={setNote}
        placeholder="Day three is thin. What do students actually do between 10 and noon?" />
      <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
        <button className="btn" onClick={decide(true)} disabled={busy}>Approve</button>
        <button className="btn btn--ghost" onClick={decide(false)} disabled={busy}>Send back</button>
      </div>
    </section>
  )
}
